import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { translateBackendError } from '@/api/translateBackendError';
import { BrandLogo } from '@/components/common/BrandLogo';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/passwordInput';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { LegalLinks } from '@/components/legal/LegalLinks';
import { useAuth } from '@/hooks/useAuth';
import { useHostWorkspace } from '@/hooks/useHostWorkspace';
import { resolveHomeRoute } from '@/lib/resolveHomeRoute';

export function LoginScreen() {
  const hostWorkspace = useHostWorkspace();
  const { t } = useTranslation(['auth', 'admin', 'onboarding']);
  const {
    status,
    platformPermissions,
    activeScopeCode,
    logInWithCredentials,
    hasSessionExpiredNotice,
    acknowledgeSessionExpiredNotice,
  } = useAuth();

  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<unknown>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSessionExpiredNotice] = useState(hasSessionExpiredNotice);

  useEffect(
    function acknowledgeSessionExpiredNoticeOnce() {
      if (hasSessionExpiredNotice === true) {
        acknowledgeSessionExpiredNotice();
      }
    },
    [hasSessionExpiredNotice, acknowledgeSessionExpiredNotice]
  );

  if (status === 'authenticated') {
    return <Navigate to={resolveHomeRoute(activeScopeCode, platformPermissions)} replace />;
  }

  async function submitLogin(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setLoginError(null);
    setIsSubmitting(true);
    try {
      await logInWithCredentials(emailAddress.trim(), password);
    } catch (caughtLoginError) {
      setLoginError(caughtLoginError);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-[2.5rem] shadow-xl overflow-hidden">
        <div className="p-8 md:p-10">
          <div className="flex justify-center mb-8">
            <BrandLogo className="h-10 w-auto object-contain" />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-zooindigo">{t('auth:welcomeBack')}</h1>
            <p className="text-sm text-zoomuted mt-2">{t('auth:signInPrompt')}</p>
          </div>

          {showSessionExpiredNotice === true ? (
            <div
              role="status"
              className="mb-4 flex items-center gap-2 px-3.5 py-2.5 bg-zoowarningbg border border-zoowarningtext rounded-xl text-xs font-medium text-zoowarningtext"
            >
              <Icon name="AlertTriangle" />
              {t('auth:sessionExpiredNotice')}
            </div>
          ) : null}

          <form onSubmit={submitLogin} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="login-email">{t('auth:emailLabel')}</Label>
              <Input
                id="login-email"
                type="email"
                required
                value={emailAddress}
                placeholder={t('auth:emailPlaceholder')}
                onChange={function updateEmail(changeEvent) {
                  setEmailAddress(changeEvent.target.value);
                }}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="login-password">{t('auth:passwordLabel')}</Label>
              <PasswordInput
                id="login-password"
                required
                value={password}
                placeholder={t('auth:passwordPlaceholder')}
                onChange={function updatePassword(changeEvent) {
                  setPassword(changeEvent.target.value);
                }}
              />
            </div>

            <div className="text-right">
              <Button asChild variant="link" className="text-xs">
                <Link to={APPLICATION_ROUTES.forgotPassword}>{t('auth:forgotPassword')}</Link>
              </Button>
            </div>

            {loginError !== null ? (
              <div
                role="alert"
                className="flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
              >
                <Icon name="AlertCircle" />
                {translateBackendError(t, loginError)}
              </div>
            ) : null}

            <Button type="submit" size="large" isLoading={isSubmitting} className="w-full">
              {t('auth:signIn')}
            </Button>
          </form>

          {hostWorkspace === null ? (
            <p className="mt-7 text-center text-sm text-zoomuted">
              {t('onboarding:signUp.noWorkspacePrompt')}{' '}
              <Button asChild variant="link">
                <Link to={APPLICATION_ROUTES.signUp}>{t('onboarding:signUp.createWorkspace')}</Link>
              </Button>
            </p>
          ) : null}
          <LegalLinks className="mt-4" />
        </div>
      </div>
    </div>
  );
}
